/**
 * Envío MC-2 · aviso dentro del estudio cuando Claude (conectado por MCP) pide
 * permiso para modificar un proyecto hecho en la app.
 *
 * - Consulta los pedidos cada 20 s, sólo con la pestaña visible (una lectura liviana).
 * - Aprobar es SIEMPRE un clic del usuario acá: ninguna herramienta MCP puede hacerlo.
 * - Mientras Claude tenga permiso sobre el proyecto abierto, trae sus cambios
 *   cada 8 s con el mismo merge seguro que usa LMX (no pisa lo del usuario).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ShieldCheck, X } from 'lucide-react';
import { toast } from 'sonner';
import { claudeAccessService, hasActiveGrant } from '../../services/claudeAccessService';
import { useWorkflowStore } from '../../store/workflowStore';
import '../../styles/claude-access.css';

const PENDING_MS = 20000;
const GRANTS_MS = 120000;
const SYNC_MS = 8000;

const visible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

export default function ClaudeAccessGate() {
  const [pending, setPending] = useState([]);
  const [grants, setGrants] = useState([]);
  const [busy, setBusy] = useState(null);
  const [hidden, setHidden] = useState(() => new Set());
  const currentProjectId = useWorkflowStore((s) => s.currentProjectId);
  const alive = useRef(true);

  const loadPending = useCallback(async () => {
    if (!visible()) return;
    try {
      const rows = await claudeAccessService.pending();
      if (alive.current) setPending(rows);
    } catch (_e) { /* sin red o sin migración: no molesta */ }
  }, []);

  const loadGrants = useCallback(async () => {
    if (!visible()) return;
    try {
      const rows = await claudeAccessService.grants();
      if (alive.current) setGrants(rows);
    } catch (_e) { /* idem */ }
  }, []);

  useEffect(() => {
    alive.current = true;
    loadPending();
    loadGrants();
    const a = setInterval(loadPending, PENDING_MS);
    const b = setInterval(loadGrants, GRANTS_MS);
    const onVis = () => { if (visible()) { loadPending(); loadGrants(); } };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      alive.current = false;
      clearInterval(a); clearInterval(b);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [loadPending, loadGrants]);

  // Cambios de Claude en vivo sobre el proyecto abierto, sólo mientras tenga permiso.
  const syncing = !!currentProjectId && hasActiveGrant(grants, currentProjectId);
  useEffect(() => {
    if (!syncing) return undefined;
    const tick = () => {
      if (!visible()) return;
      const ws = useWorkflowStore.getState();
      ws.syncFromAgent?.();
    };
    const id = setInterval(tick, SYNC_MS);
    return () => clearInterval(id);
  }, [syncing]);

  const decide = async (req, action) => {
    setBusy(req.id);
    try {
      if (action === 'deny') {
        await claudeAccessService.deny(req.id);
        toast.success('Listo: Claude no va a modificar ese proyecto.');
      } else {
        await claudeAccessService.approve(req.id, action);
        toast.success(action === '1h'
          ? `Claude puede editar «${req.project_name}» durante 1 hora.`
          : `Claude puede editar «${req.project_name}». Lo revocás en Configuración → Claude.`);
        loadGrants();
      }
      setPending((p) => p.filter((r) => r.id !== req.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'No se pudo guardar tu respuesta.');
      loadPending();
    } finally {
      setBusy(null);
    }
  };

  const shown = pending.filter((r) => !hidden.has(r.id));
  if (!shown.length) return null;
  const req = shown[0];

  return (
    <div className="ca-gate" role="dialog" aria-modal="false" aria-labelledby="ca-gate-title"
         data-testid="claude-access-gate">
      <button type="button" className="ca-gate-x" aria-label="Decidir después"
              onClick={() => setHidden((h) => new Set(h).add(req.id))}>
        <X size={16} />
      </button>
      <div className="ca-gate-head">
        <span className="ca-gate-icon" aria-hidden="true"><ShieldCheck size={18} /></span>
        <h2 id="ca-gate-title">Claude pide editar «{req.project_name}»</h2>
      </div>
      {req.reason && <p className="ca-gate-reason">«{req.reason}»</p>}
      <p className="ca-gate-note">
        Antes de su primer cambio se guarda una copia del proyecto: la podés restaurar
        en Configuración → Claude.{req.connection_name ? ` Conexión: ${req.connection_name}.` : ''}
      </p>
      <div className="ca-gate-actions">
        <button type="button" className="ca-btn ca-btn-primary" disabled={busy === req.id}
                onClick={() => decide(req, '1h')} data-testid="claude-access-1h">
          Permitir 1 hora
        </button>
        <button type="button" className="ca-btn" disabled={busy === req.id}
                onClick={() => decide(req, 'project')} data-testid="claude-access-project">
          Permitir siempre en este proyecto
        </button>
        <button type="button" className="ca-btn ca-btn-ghost" disabled={busy === req.id}
                onClick={() => decide(req, 'deny')} data-testid="claude-access-deny">
          Rechazar
        </button>
      </div>
      {shown.length > 1 && <p className="ca-gate-more">+{shown.length - 1} pedido(s) más</p>}
    </div>
  );
}
